// Reception date bar — RON-5469.
// Prototype region: `.date-bar` (`#date-prev`, `#date-display`, `#date-next`, `#date-today`).
//
// The date is the PAGE'S STATE, not a filter (§3): it combines with `filter` and `q` as one of
// three narrowing conditions on a single query, and it is what the whole board is "for".

import { useTranslation } from "react-i18next";

// ISO in, ISO out, so nothing here ever constructs a Date from a string and inherits a timezone.
// `new Date("2026-04-28")` is UTC midnight, which is the previous day in any negative offset —
// the classic way a date bar goes one day out for half the world.
const shiftISODate = (iso, days) => {
  const [year, month, day] = String(iso).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

const formatLong = (iso) => {
  const [year, month, day] = String(iso).split("-").map(Number);

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-IE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const ReceptionDateBar = ({ date, isToday, todayISO, onDateChange }) => {
  const { t } = useTranslation("common");

  if (!date) return null;

  return (
    <section className="reception-datebar">
      <span className="reception-datebar__eyebrow">{t("react_reception_showing_for")}</span>

      <button
        type="button"
        className="reception-datebar__arrow"
        onClick={() => onDateChange?.(shiftISODate(date, -1))}
        aria-label={t("react_reception_previous_day")}
        title={t("react_reception_previous_day")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
      </button>

      {/* A native date input rather than a custom picker: it is the one control a receptionist
          already knows, it is keyboard-accessible for free, and the app's shared CustomDatePicker
          pulls in nine date-fns locales that this standalone tab has no other use for. */}
      <label className="reception-datebar__display">
        <span className="reception-datebar__text">{formatLong(date)}</span>
        {/* `is_today` comes from the server so this pill never compares clocks with it (§3). */}
        {isToday && <span className="reception-datebar__pill">{t("react_reception_today")}</span>}
        <input
          type="date"
          className="reception-datebar__input"
          value={date}
          onChange={(event) => event.target.value && onDateChange?.(event.target.value)}
          aria-label={t("react_reception_pick_date")}
        />
      </label>

      <button
        type="button"
        className="reception-datebar__arrow"
        onClick={() => onDateChange?.(shiftISODate(date, 1))}
        aria-label={t("react_reception_next_day")}
        title={t("react_reception_next_day")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      </button>

      {/* Offered only when it would do something. A "jump to today" that is already today is a
          control that answers a question nobody asked. */}
      {!isToday && (
        <button
          type="button"
          className="reception-btn reception-btn--ghost reception-datebar__today"
          onClick={() => onDateChange?.(todayISO)}
        >
          {t("react_reception_jump_today")}
        </button>
      )}
    </section>
  );
};

export default ReceptionDateBar;
